import api, { cachedGet } from './apiClient';
import type { ApiResponse } from 'shared/types/api';

// Plans are a small, slow-changing catalog (PricingPlan is versioned —
// editing a plan creates a new version). Cache aggressively to avoid
// re-fetching on every mount of PlansComparison, ConfiguracionPage,
// MyBilling.
const PLAN_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export type PlanFamily = 'basic' | 'business';
export type PlanTier = 20 | 25 | 75 | 200 | 500 | 999999;

export interface PlanFeatures {
  maxUsers: number;
  maxContractors: number;
  maxWorkPermitsPerMonth: number;
  maxStorageGB: number;
  maxFileSizeMB: number;
  includesTalentLMSIntegration: boolean;
  includesAdvancedReports: boolean;
  includesAPIAccess: boolean;
  includesCustomBranding: boolean;
  includesPrioritySupport: boolean;
  includesMultipleCompanies: boolean;
  maxCompanies: number;
  includesAuditLog: boolean;
  includesBulkOperations: boolean;
  includesCustomFields: boolean;
  includesSSO: boolean;
  dataRetentionDays: number;
  allowedNotificationChannels: string[];
  maxNotificationsPerMonth: number;
  includesReviewModule: boolean;
  maxReviewsPerMonth: number;
  detailedRatings: boolean;
  /** Marketplace access (browse provider directory, contact providers). */
  includesMarketplace?: boolean;
  /** Bidding system — business-only. Feature not yet built; flag is
   *  reserved so the future UI can gate cleanly. */
  includesBidding?: boolean;
}

export interface Plan {
  _id: string;
  name: string;
  code: string;
  description: string;
  family: PlanFamily;
  tier: PlanTier;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  features: PlanFeatures;
  isActive: boolean;
  isPopular: boolean;
  sortOrder: number;
  customLimits?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface Subscription {
  _id: string;
  company: any;
  plan: Plan | string;
  status:
    | 'pending_first_payment'
    | 'pending'
    | 'active'
    | 'past_due'
    | 'suspended_unpaid'
    | 'cancelled'
    | 'expired';
  billingCycle: 'monthly' | 'yearly';
  courtesyExtraUsers?: number;
  courtesyExpiresAt?: string | null;
  courtesyReason?: string;
  courtesyGrantsThisYear?: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  usage: {
    currentUsers: number;
    currentContractors: number;
    workPermitsThisMonth: number;
    storageUsedGB: number;
    notificationsSentThisMonth: number;
    reviewsThisMonth: number;
    lastUpdated: string;
  };
  customLimits?: Record<string, any>;
  discountPercentage?: number;
  autoRenew: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SeatUsage {
  ownUsers: number;
  coveredProviderUsers: number;
  providerBreakdown: Array<{
    providerId: string;
    providerName: string;
    assignedCount: number;
  }>;
  effectiveSeats: number;
  courtesyExtra: number;
  effectiveCap: number | null;
}

export const planApi = {
  getAll: async (): Promise<Plan[]> => {
    const res = await cachedGet<ApiResponse<Plan[]>>('/plans', undefined, PLAN_CACHE_TTL);
    return res.data!;
  },
  getById: async (id: string): Promise<Plan> => {
    const res = await cachedGet<ApiResponse<Plan>>(`/plans/${id}`, undefined, PLAN_CACHE_TTL);
    return res.data!;
  },
};

export const subscriptionApi = {
  // Returns pagination alongside data (mirrors the {data, pagination}
  // convention used by other paginated list methods across the service
  // layer, e.g. dashboardApi.getUsersDashboard / marketplaceApi.listProviders)
  // so callers can surface `truncated`/`total` instead of it being silently
  // discarded.
  getAll: async (): Promise<{ data: Subscription[]; pagination?: ApiResponse<Subscription[]>['pagination'] }> => {
    const res = await api.get<ApiResponse<Subscription[]>>('/subscriptions');
    return { data: res.data || [], pagination: res.pagination };
  },
  getByCompany: async (companyId: string): Promise<Subscription | null> => {
    try {
      const res = await api.get<ApiResponse<Subscription>>(`/subscriptions/company/${companyId}`);
      return res.data || null;
    } catch {
      return null;
    }
  },
  getSeatUsage: async (companyId: string): Promise<SeatUsage> => {
    const res = await api.get<ApiResponse<SeatUsage>>(`/subscriptions/company/${companyId}/seat-usage`);
    return res.data!;
  },
  create: async (data: { companyId: string; planId: string; billingCycle: 'monthly' | 'yearly' }): Promise<Subscription> => {
    const res = await api.post<ApiResponse<Subscription>>('/subscriptions', data);
    return res.data!;
  },
  update: async (id: string, data: Partial<Pick<Subscription, 'status' | 'customLimits' | 'billingCycle' | 'notes' | 'autoRenew' | 'discountPercentage'> & { plan?: string }>): Promise<Subscription> => {
    const res = await api.patch<ApiResponse<Subscription>>(`/subscriptions/${id}`, data);
    return res.data!;
  },
  grantCourtesy: async (id: string, data: { extraUsers: number; months: 1 | 2 | 3; reason?: string }): Promise<Subscription> => {
    const res = await api.post<ApiResponse<Subscription>>(`/subscriptions/${id}/courtesy`, data);
    return res.data!;
  },
  revokeCourtesy: async (id: string): Promise<Subscription> => {
    const res = await api.delete<ApiResponse<Subscription>>(`/subscriptions/${id}/courtesy`);
    return res.data!;
  },
  // Borra el plan de la empresa y desactiva el acceso de sus usuarios a
  // ESTA empresa (no a otras a las que también pertenezcan). Reversible:
  // asignar un plan nuevo reactiva automáticamente lo que este borrado
  // desactivó. Solo super_admin / Soporte Monitor de su portafolio.
  deselectPlan: async (companyId: string): Promise<{ deactivatedUserCount: number }> => {
    const res = await api.delete<ApiResponse<{ deactivatedUserCount: number }>>(`/subscriptions/company/${companyId}`);
    return res.data!;
  },
};
